import {
  FieldBooleanResponse,
  FieldWithOptionsResponse,
  MeasurementCardResponse,
  OptionResponse,
  StackFilterOptionsResponse,
} from '.generated/api';
import { apiTranslate } from 'translations/translations';

export type TranslatedFilterOptions = {
  registrators?: {
    title: string;
    options: Array<{ label: string; value: string }>;
  };
  surveyors?: {
    title: string;
    options: Array<{ label: string; value: string }>;
  };
  treeSpecies?: {
    title: string;
    options: Array<{ label: string; value: string }>;
  };
  assortments?: {
    title: string;
    options: Array<{ label: string; value: string }>;
  };
  assortmentLength?: {
    title: string;
  };
  areaNumber?: {
    title: string;
  };
  fullMeasurement?: {
    title: string;
    trueLabel: string;
    falseLabel: string;
  };
  syncWithLesnikPlus?: {
    title: string;
    trueLabel: string;
    falseLabel: string;
  };
};

const translateFieldOptions = (
  optionIds?: string[],
  fieldOptions?: OptionResponse[],
) => {
  if (!(optionIds && fieldOptions)) {
    return [];
  }

  const translatedOptions: { label: string; value: string }[] = [];
  for (const optionId of optionIds) {
    const option = fieldOptions.find(
      (fieldOption) => optionId === fieldOption.key,
    );
    if (option) {
      translatedOptions.push({
        value: option.key,
        label: apiTranslate(option.text),
      });
    }
  }

  return translatedOptions;
};

export const translateFilterOptions = (
  measurementCard: MeasurementCardResponse,
  filterOptions: StackFilterOptionsResponse,
) => {
  const translatedOptions: TranslatedFilterOptions = {};

  {
    const registratorField = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.registrator,
    );
    if (registratorField) {
      translatedOptions.registrators = {
        title: apiTranslate(registratorField.title),
        options:
          filterOptions.registrators?.map((option) => ({
            label: option,
            value: option,
          })) ?? [],
      };
    }
  }

  {
    const surveyorField = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.surveyor,
    );
    if (surveyorField) {
      translatedOptions.surveyors = {
        title: apiTranslate(surveyorField.title),
        options:
          filterOptions.surveyors?.map((option) => ({
            label: option,
            value: option,
          })) ?? [],
      };
    }
  }

  {
    const treeSpeciesField = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.treeSpecies,
    ) as FieldWithOptionsResponse;
    if (treeSpeciesField) {
      translatedOptions.treeSpecies = {
        title: apiTranslate(treeSpeciesField.title),
        options: translateFieldOptions(
          filterOptions.treeSpecies,
          treeSpeciesField.options ?? undefined,
        ),
      };
    }
  }

  {
    const assortmentField = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.assortment,
    ) as FieldWithOptionsResponse;
    if (assortmentField) {
      translatedOptions.assortments = {
        title: apiTranslate(assortmentField.title),
        options: translateFieldOptions(
          filterOptions.assortments,
          assortmentField.options ?? undefined,
        ),
      };
    }
  }

  {
    const assortmentLength = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.assortmentLength,
    );
    if (assortmentLength) {
      translatedOptions.assortmentLength = {
        title: apiTranslate(assortmentLength.title),
      };
    }
  }

  {
    const areaNumber = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.areaNumber,
    );
    if (areaNumber) {
      translatedOptions.areaNumber = {
        title: apiTranslate(areaNumber.title),
      };
    }
  }

  {
    const fullMeasurement = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.fullMeasurement,
    ) as FieldBooleanResponse;
    if (
      fullMeasurement &&
      fullMeasurement.trueLabel &&
      fullMeasurement.falseLabel
    ) {
      translatedOptions.fullMeasurement = {
        title: apiTranslate(fullMeasurement.title),
        trueLabel: apiTranslate(fullMeasurement.trueLabel),
        falseLabel: apiTranslate(fullMeasurement.falseLabel),
      };
    }
  }

  {
    const syncWithLesnikPlus = measurementCard.fields.find(
      (field) => field.id === filterOptions.fieldIds.syncWithLesnikPlus,
    ) as FieldBooleanResponse;
    if (
      syncWithLesnikPlus &&
      syncWithLesnikPlus.trueLabel &&
      syncWithLesnikPlus.falseLabel
    ) {
      translatedOptions.syncWithLesnikPlus = {
        title: apiTranslate(syncWithLesnikPlus.title),
        trueLabel: apiTranslate(syncWithLesnikPlus.trueLabel),
        falseLabel: apiTranslate(syncWithLesnikPlus.falseLabel),
      };
    }
  }

  return translatedOptions;
};
